// Feedback read state: per-user "last read" cursor per conversation, used to highlight new replies.
// Stored in localStorage for now; swap load/saveReadState for a server table to sync devices.

import type { FeedbackMessage } from '../types';

export type MessageActivity = Pick<
  FeedbackMessage,
  'conversation_id' | 'sender_id' | 'created_at' | 'message_type'
>;

export interface ReadState {
  /** Messages before this are treated as read (set the first time a user is seen) */
  baseline: string;
  /** conversation_id -> created_at of the newest message the user has seen */
  lastRead: Record<string, string>;
}

const storageKey = (userId: string) => `vehix:feedback-read:${userId}`;
const listeners = new Set<() => void>();

const toTime = (iso: string | undefined) => (iso ? new Date(iso).getTime() : 0);

export const loadReadState = (userId: string, now: Date = new Date()): ReadState => {
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<ReadState>;
      if (typeof parsed.baseline === 'string') {
        return { baseline: parsed.baseline, lastRead: parsed.lastRead ?? {} };
      }
    }
  } catch {
    // Storage blocked or corrupt: fall through to a fresh baseline
  }
  // First visit: everything that already exists counts as read, only new replies highlight
  const fresh: ReadState = { baseline: now.toISOString(), lastRead: {} };
  saveReadState(userId, fresh);
  return fresh;
};

export const saveReadState = (userId: string, state: ReadState): void => {
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(state));
  } catch {
    // Storage unavailable: read state lasts for this page view only
  }
};

/** Everything at or before this time in the conversation has been seen */
export const getReadCursor = (state: ReadState, conversationId: string): string => {
  const lastRead = state.lastRead[conversationId];
  return lastRead && toTime(lastRead) > toTime(state.baseline) ? lastRead : state.baseline;
};

/** Record that the user has seen the conversation up to `readAt` (a message's created_at) */
export const markConversationRead = (userId: string, conversationId: string, readAt: string) => {
  const state = loadReadState(userId);
  if (toTime(readAt) <= toTime(getReadCursor(state, conversationId))) return;
  saveReadState(userId, { ...state, lastRead: { ...state.lastRead, [conversationId]: readAt } });
  listeners.forEach(listener => listener());
};

/** Re-render when read state changes in this tab or another tab */
export const subscribeToReadState = (listener: () => void): (() => void) => {
  listeners.add(listener);
  const onStorage = (e: StorageEvent) => {
    if (e.key?.startsWith('vehix:feedback-read:')) listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
};

/** A reply counts as unread when someone else sent it after the user's cursor */
export const isUnreadMessage = (
  message: MessageActivity,
  userId: string,
  state: ReadState
): boolean =>
  message.sender_id !== userId &&
  message.message_type !== 'admin_note' &&
  toTime(message.created_at) > toTime(getReadCursor(state, message.conversation_id));

/** conversation_id -> number of unread replies */
export const countUnreadByConversation = (
  activity: MessageActivity[],
  userId: string,
  state: ReadState
): Record<string, number> => {
  const counts: Record<string, number> = {};
  for (const message of activity) {
    if (isUnreadMessage(message, userId, state)) {
      counts[message.conversation_id] = (counts[message.conversation_id] ?? 0) + 1;
    }
  }
  return counts;
};
